import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { Box, Container, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useUnit } from 'effector-react';
import moment from 'moment';

import { SearchInstrumentField } from 'components/SearchInstrumentField';
import { PortfolioSignal } from 'types/portfolioSignal';

import { Form } from './components/Form';
import { PackagePositionList } from './components/PackagePositionList';
import { Params } from './components/Params';
import { PositionList } from './components/PositionList';
import { Tab, Tabs } from './components/Tabs';
import { tabs } from './constants';
import { useSignalActions } from './hooks/useSignalActions';
import { useSignalForm } from './hooks/useSignalForm';
import { onboardingConfigs } from './onboardingConfig';
import { getLimitOrderTimeoutMax } from './timeout';
import { FormFields, MarketSignal, SubmissionState } from './types';
import { OrderBook } from '../../../../components/OrderBook';
import { StepByStepHints } from '../../../../components/StepByStepHints';
import { Search$, searchApi } from '../../../../effector/search';
import { Strategies$ } from '../../../../effector/strategies';
import { User$ } from '../../../../effector/user';
import { Position } from '../../../../types/position';
import { RecalculationMode } from '../../../../types/strategy';
import { formatMoney } from '../../../../utils';
import { isSuccessResponse, parseApiError } from '../../../../utils/apiErrors';
import { useLoadPositions } from '../../Positions/queries';
import { useComment, useMultipleMarketSignals, usePortfolioSignal, useSignalSettings } from '../queries';

type Props = {
  strategyId: string;
  onClose: () => void;
  onApprove: () => void;
  onRestartRef?: React.MutableRefObject<(() => void) | null>;
};

const isTransportError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');

  return /failed to fetch|networkerror|network request failed|load failed|aborterror|timeout|timed out/i.test(message);
};

export const AddSignalForm: React.FC<Props> = ({ strategyId, onClose, onApprove, onRestartRef }) => {
  const [isSetData, setIsSetData] = useState(false);
  const [error, setError] = useState('');
  const [active, setTab] = useState(tabs[0]);
  const [clearSearch, setClearSearch] = useState<boolean>(false);
  const [listSignals, setListSignals] = useState<MarketSignal[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editablePosition, setEditablePosition] = useState<Position | null>(null);
  const [forceSearchValue, setForceSearchValue] = useState<string>('');
  const [positions, setPositions] = useState<Position[]>([]);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number } | null>(null);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const submitLockRef = useRef(false);

  const user = useUnit(User$);
  const searchStore = useUnit(Search$);
  const strategies = useUnit(Strategies$);

  const queryClient = useQueryClient();

  const details = searchStore?.details;

  const { data: signalSettingsData } = useSignalSettings(active.id === 5);

  const limitOrderTimeoutMax = useMemo(
    () => getLimitOrderTimeoutMax(signalSettingsData?.result, details?.classCode),
    [signalSettingsData?.result, details?.classCode],
  );

  const strategy = useMemo(() => strategies.find((item) => item.id === strategyId), [strategies, strategyId]);

  const { data } = useLoadPositions(strategyId as string, strategy?.status);

  const { control, values, errors, comment, handleSubmit, setValue, resetForm } = useSignalForm(
    active.id,
    editablePosition,
    priceRange,
    limitOrderTimeoutMax,
  );

  const refreshSignalState = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['checkSignal', strategyId] });
    queryClient.invalidateQueries({ queryKey: ['portfolio', strategyId, strategy?.status] });
    queryClient.invalidateQueries({ queryKey: ['positions', strategyId, strategy?.status] });
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) && query.queryKey[0] === 'loadDeals' && query.queryKey[1] === strategyId,
    });
  }, [queryClient, strategy?.status, strategyId]);

  const beginSubmission = useCallback(() => {
    if (submitLockRef.current || submissionState !== 'idle') {
      return false;
    }

    submitLockRef.current = true;
    setSubmissionState('pending');
    setError('');
    return true;
  }, [submissionState]);

  const resetSubmissionLock = useCallback(() => {
    submitLockRef.current = false;
    setSubmissionState('idle');
  }, []);

  const handleError = useCallback(
    (result: unknown) => {
      setError(parseApiError(result));
      refreshSignalState();

      if (isTransportError(result)) {
        setSubmissionState('unknown');
      } else {
        resetSubmissionLock();
      }
    },
    [refreshSignalState, resetSubmissionLock],
  );

  const handleSuccess = useCallback(
    (response: unknown) => {
      if (!isSuccessResponse(response)) {
        setError(parseApiError(response));
        refreshSignalState();
        resetSubmissionLock();
        return;
      }

      // При подаче сигнала инвалидируем все связанные данные:
      // 1. Портфель
      queryClient.invalidateQueries({ queryKey: ['portfolio', strategyId, strategy?.status] });
      // 2. Открытые позиции
      queryClient.invalidateQueries({ queryKey: ['positions', strategyId, strategy?.status] });
      // 3. Отложенные сигналы
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'delayed' && query.queryKey[1] === strategyId,
      });
      // 4. История сделок
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'loadDeals' && query.queryKey[1] === strategyId,
      });

      resetSubmissionLock();
      onClose();
      onApprove();
    },
    [onApprove, onClose, queryClient, refreshSignalState, resetSubmissionLock, strategy?.status, strategyId],
  );

  const handleAllowRetry = useCallback(() => {
    setError('');
    resetSubmissionLock();
    refreshSignalState();
  }, [refreshSignalState, resetSubmissionLock]);

  const {
    isLoadingMarketOrDelayedSignal,
    createMarketSignal,
    sendMarketSignal,
    sendMarketLimitSignal,
    sendDelayedSignal,
    onAddPositionToPortfolioSignal,
    onUpdatePortfolioPositionSignal,
  } = useSignalActions({
    strategyId: strategyId as string,
    details: details || null,
    comment,
    setHasChanges,
    setPositions,
    positions,
    editablePosition,
    forceClearSearch: () => {
      setClearSearch(true);
      setTimeout(() => setClearSearch(false), 300);
    },
    onSuccess: handleSuccess,
    onError: handleError,
    beginSubmission,
  });

  const { data: commentData } = useComment(strategyId, moment().format('YYYY-MM-DD'));

  useEffect(() => {
    if (commentData?.result?.comment) {
      setValue('description', commentData?.result?.comment);
    }
  }, [commentData?.result?.comment, setValue]);

  useEffect(() => {
    if (!isSetData) {
      setPositions(data?.result || []);
      setIsSetData(true);
    }
  }, [isSetData, data]);

  useEffect(() => {
    return () => {
      searchApi.clearDetails('');
    };
  }, []);

  useEffect(() => {
    if (!details) {
      resetForm(active.id, { description: values.description });
      setEditablePosition(null);
    }
  }, [active.id, details, resetForm, values.description]);

  const handleTabChange = useCallback(
    (nextTab: Tab) => {
      if (nextTab.id === active.id) {
        return;
      }

      setTab(nextTab);
      setEditablePosition(null);
      setPriceRange(null);
      resetForm(nextTab.id, { description: values.description });
      if (submissionState !== 'unknown') {
        setError('');
      }
      setClearSearch(true);
      setTimeout(() => setClearSearch(false), 300);
    },
    [active.id, resetForm, submissionState, values.description],
  );

  const params = useMemo(
    () => ({
      title: 'Параметры инструмента',
      items: [
        {
          name: 'тикер',
          value: searchStore?.details?.key,
          className: '',
        },
        {
          name: 'код класса',
          value: searchStore?.details?.classCode,
        },
        {
          name: 'текущая стоимость',
          value:
            searchStore?.details?.quotation != null
              ? searchStore.details.quotation
              : formatMoney(
                  searchStore?.details?.price,
                  searchStore?.details?.currency,
                  searchStore?.details?.priceDigits,
                ),
        },
        {
          name: 'размер лота',
          value: searchStore?.details?.lotSize ? `${searchStore?.details?.lotSize} шт` : '',
        },
        {
          name: 'шорт',
          value: searchStore?.details?.shortAllowed ? 'доступен' : 'не доступен',
          className: searchStore?.details?.shortAllowed ? 'rise' : 'fall',
        },
      ],
    }),
    [searchStore],
  );

  const params1 = useMemo(
    () => ({
      title: 'Параметры сигнала',
      items: [
        {
          name: 'минимальный объем',
          value: `${searchStore?.details?.minWeight}%`,
          className: 'rise',
        },
        {
          name: 'максимальный объем',
          value: `${searchStore?.details?.maxWeight}%`,
          className: 'fall',
        },
        {
          name: 'текущая позиция',
          value: `${searchStore?.details?.currentWeight}%`,
          className: 'rise',
        },
      ],
    }),
    [searchStore],
  );

  const saleIsDisabled = useMemo(() => !details?.shortAllowed && details?.currentWeight === 0, [details]);
  const closeIsDisabled = useMemo(() => details?.currentWeight === 0, [details]);

  const disableFields: number[] = [];
  if (saleIsDisabled) {
    disableFields.push(2);
  }
  if (closeIsDisabled) {
    disableFields.push(3);
  }

  const { mutate: multipleSignals, isPending: isLoadingMultipleSignals } = useMultipleMarketSignals({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const { mutate: portfolioSignal, isPending: isLoadingPortfolioSignal } = usePortfolioSignal({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const onRemoveSignals = useCallback(
    (signal: MarketSignal) => {
      const signals = listSignals.filter((item) => item.symbol !== signal.symbol);
      setListSignals(signals);
    },
    [listSignals],
  );

  const onSearchClick = useCallback(
    (securityKey: string) => {
      searchApi.getPosition({ securityKey, strategyId });
      searchApi.clearSuggestion('');
    },
    [strategyId],
  );

  const onEditPortfolioPositionSignal = useCallback(
    (securityKey: string) => {
      const position = positions.find((item) => item.securityKey === securityKey);
      if (position) {
        setEditablePosition(position);
        onSearchClick(position.securityKey);
      }
      setError('');
    },
    [positions, onSearchClick],
  );

  const onRemovePortfolioPositionSignal = useCallback(
    (position: Position) => {
      setHasChanges(true);
      setPositions(positions.filter((item) => item.securityKey !== position.securityKey));
    },
    [positions],
  );

  const onSavePortfolioChanges = useCallback(() => {
    const isWeightedAverage =
      strategy?.recalcMode === RecalculationMode.WeightedAverageWithRecalculation ||
      strategy?.recalcMode === RecalculationMode.WeightedAverageWithoutRecalculation;

    const updatedPositions = positions.map((position) => ({
      securityKey: position.securityKey,
      weight: isWeightedAverage ? Number(position.weight) : Number(position.currentWeight),
      comment,
    }));

    const data: PortfolioSignal = {
      stratId: strategy?.id as string,
      managerId: user?.id as number,
      positions: updatedPositions,
    };

    if (beginSubmission()) {
      portfolioSignal(data);
    }
  }, [beginSubmission, positions, strategy?.id, strategy?.recalcMode, user?.id, portfolioSignal, comment]);

  const onAddSignal = useCallback(
    (values: FormFields) => {
      const signal = createMarketSignal(values);
      const found = listSignals.find((item) => item.symbol === signal.symbol);

      if (!found) {
        const signals = [...listSignals, signal];
        setListSignals(signals);
        setClearSearch(true);
        setTimeout(() => setClearSearch(false), 300);
      } else {
        setError('Данный инструмент уже добавлен в список');
      }
    },
    [createMarketSignal, listSignals],
  );

  const sendMultiple = useCallback(() => {
    const data = listSignals.map((item) => ({
      ...item,
      weight: Number(item.weight),
      comment,
    }));
    if (beginSubmission()) {
      multipleSignals(data);
    }
  }, [beginSubmission, listSignals, comment, multipleSignals]);

  const handlePriceRangeChange = useCallback((minPrice: number, maxPrice: number) => {
    setPriceRange({ min: minPrice, max: maxPrice });
  }, []);

  const handlePriceSelect = useCallback(
    (price: number) => {
      setValue('price', price.toString());
    },
    [setValue],
  );

  const onSubmit = handleSubmit((data: FormFields) => {
    if (active.id === 1) {
      sendMarketSignal(data);
    } else if (active.id === 4) {
      onAddPositionToPortfolioSignal(data);
    } else if (active.id === 5) {
      sendMarketLimitSignal(data);
    } else {
      sendDelayedSignal(data);
    }
  });

  const handleClearSearch = useCallback(() => {
    setForceSearchValue('');
  }, []);

  const securityKey = useMemo(() => {
    if (!details?.symbol || !details?.classCode) {
      return '';
    }

    return `${details.symbol} ${details.classCode} QUIK`;
  }, [details]);

  const loading = isLoadingPortfolioSignal || isLoadingMultipleSignals || isLoadingMarketOrDelayedSignal;

  const isPackageTab = active.id === tabs[2].id;
  const isPositionTab = active.id === tabs[3].id;
  const isOrderBookTab = active.id === tabs[4].id;
  const isOrderBook = isOrderBookTab && securityKey;

  const searchClassNames: Record<number, string> = {
    1: 'market-signal-search',
    2: 'delayed-signal-search',
    3: 'package-signal-search',
    4: 'portfolio-signal-search',
    5: 'limit-order-search-field',
  };
  const searchFieldClassName = searchClassNames[active.id] || '';

  const onboardingConfig = onboardingConfigs[active.id];

  return (
    <Container maxWidth="xl" sx={{ minWidth: 1000 }}>
      <Box sx={{ width: '100%', mt: 2 }}>
        <Tabs tabs={tabs} activeTab={active} onTabChange={handleTabChange} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={isPackageTab || isPositionTab || isOrderBook ? 8 : 12}>
            <SearchInstrumentField
              strategyId={strategyId}
              className={searchFieldClassName}
              forceSearchValue={forceSearchValue}
              forceClear={clearSearch}
              onSearch={onSearchClick}
              onClear={handleClearSearch}
            />
            {details && <Params params={[params, params1]} />}
            <Form
              control={control}
              values={values}
              errors={errors}
              activeTabId={active.id}
              loading={loading}
              details={details || null}
              editablePosition={editablePosition}
              error={error}
              limitOrderTimeoutMax={limitOrderTimeoutMax}
              onSubmit={onSubmit}
              onAddSignal={onAddSignal}
              onUpdatePortfolioPositionSignal={onUpdatePortfolioPositionSignal}
              packageSignalsCount={listSignals.length}
              onSubmitPackageSignals={sendMultiple}
              hasPortfolioChanges={hasChanges}
              onSavePortfolioChanges={onSavePortfolioChanges}
              submissionState={submissionState}
              onAllowRetry={handleAllowRetry}
            />
          </Grid>

          {isPackageTab && (
            <Grid item xs={12} md={4}>
              <PackagePositionList items={listSignals} onRemove={onRemoveSignals} />
            </Grid>
          )}

          {isPositionTab && (
            <Grid item xs={12} md={4}>
              <PositionList
                positions={positions}
                onEditPortfolioPositionSignal={onEditPortfolioPositionSignal}
                onRemovePortfolioPositionSignal={onRemovePortfolioPositionSignal}
                recalculationMode={strategy?.recalcMode}
              />
            </Grid>
          )}

          {isOrderBook && (
            <Grid item xs={12} md={4}>
              <Box>
                <OrderBook
                  securityKey={securityKey}
                  onPriceRangeChange={handlePriceRangeChange}
                  onPriceSelect={handlePriceSelect}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      {onboardingConfig && (
        <StepByStepHints
          key={active.id}
          storageKey={onboardingConfig.storageKey}
          steps={onboardingConfig.steps}
          texts={onboardingConfig.texts}
          onRestartRef={onRestartRef}
        />
      )}
    </Container>
  );
};
